import { useContext, useMemo } from 'react';
import {
  EditContext,
  PageContext,
  PageContextType,
} from '@xingternal/pages-common';
import { useConfig } from '@xingternal/hops';
import * as Types from '@xingternal/pages-graphql-types';
import { getPathByFocusType } from '../../utils/getPathByFocusType';
import { isBrowser } from '@xingternal/hops';
import { useHistory, useRouteMatch } from 'react-router-dom';

/* this hook needs to work on SSR. this meane it can't depend on useEffect or useContext  */
const useUpdatePageContext = (
  data?: Types.Maybe<Types.EntityPageResult>,
  extraPageContext?: Partial<PageContextType>
): PageContextType | undefined => {
  const { isEditor } = useContext(EditContext);
  const config = useConfig();
  const { pageContext, setPageContext } = useContext(PageContext);
  let history = useHistory();
  const match = useRouteMatch();
  const path = match.url.split('/')[1];

  const newPageContext = useMemo(() => {
    if (data?.__typename !== 'EntityPage') {
      return pageContext;
    }

    const isFollowingPage =
      (data.userPageContext?.userInteractions?.__typename ===
        'EntityPageUserInteractionFollow' &&
        data.userPageContext.userInteractions.followState?.isFollowing) ||
      null;
    const basePath = getPathByFocusType(extraPageContext?.isWebview);

    const updatedContext = {
      basePath,
      contractType: data.contract?.type,
      publicationStatus: data.publicationStatus,
      visibility: data.visibility,
      pageId: data.id,
      url: data.links?.self,
      pageSlug: data.slug,
      pageSlogan: data.slogan || '',
      isFollowingPage: isFollowingPage,
      focusType: data.focusType!,
      companyId: data.context?.companyId,
      cppId: data.context?.cppId,
      pageTitle: data.title,
      globalId: data.globalId,
      upsellActive:
        data.contract?.type === 'FREE' &&
        isEditor &&
        data.focusType === Types.EntityPageFocusType.Company,
      logo: data.logoImage?.[0].url,
      parentPageUrl:
        data.properties?.parentPage ===
        Types.EntityPageParentPage.CompaniesLandingPage
          ? `${config.apiHost}/companies`
          : `${config.apiHost}/news`,
      isShowingCompanyBanner: false,
      goBackUrl: pageContext?.globalId
        ? history.goBack
        : () => window.location.assign(`/${path}/${data.slug}`),
      ...extraPageContext,
    };

    setPageContext((prevState) => ({
      ...prevState,
      ...updatedContext,
    }));

    return {
      ...pageContext,
      ...updatedContext,
    };
  }, [data, isEditor]);

  // client side will use the always updated pageContext; SSR will use the newPageContext
  return isBrowser ? pageContext : newPageContext;
};

export default useUpdatePageContext;
